import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { Currency } from '@sequencehq/api'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  CubeStatus,
  Customer,
  DisabledReasonType,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import { contactsToOptions } from './contactsToOptions'
import { contactToQuoteContact } from 'Cube/communication/external/quotes.api.v1/adapters/quote.adapters.in'
import { type Duration, format, isSameDay } from '@sequencehq/utils/dates'
import { inflect } from 'inflection'
import { useQuery } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { toCurrencyName } from '@sequencehq/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { QUOTE_DEAL_TYPE_LABELS } from '@sequencehq/quote-content'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import type { Option } from '@sequencehq/core-components'
import type { RecipientsValue } from '../QuoteEditorControls/RecipientsSelect/useRecipientsSelection'
import { useAmendedSchedule } from '../QuoteEditorControls/AmendmentEffectiveFrom/useAmendedSchedule'
import { useChangeAmendmentEffectiveFrom } from '../QuoteEditorControls/AmendmentEffectiveFrom/useChangeAmendmentEffectiveFrom'
import { useShiftAmendmentEffectiveFrom } from '../QuoteEditorControls/AmendmentEffectiveFrom/useShiftAmendmentEffectiveFrom'
import { hasPricingToOverwrite } from '../QuoteEditorControls/SelectScheduleForAmendment/hasPricingToOverwrite'
import type { ScheduleAmendment } from '../QuoteEditorControls/SelectScheduleForAmendment/useApplyAmendmentToQuote'
import { useApplyScheduleAmendment } from '../QuoteEditorControls/SelectScheduleForAmendment/useApplyScheduleAmendment'

type FieldConfig<Value> = {
  isDisabled: boolean
  disabledReason: string | undefined
  validationErrors: ValidationResult[]
  value: Value | undefined
  onChange: (newValue: Value) => void
  options: Array<Option<Value>>
}

type ForeverDuration = {
  diff: 'FOREVER'
  label: string
  value: 'FOREVER'
}

enum AvailableModal {
  CreateRecipient = 'createRecipient',
  SelectScheduleForAmendment = 'selectScheduleForAmendment'
}

type QuoteDealType = DashboardApi20240730.Quotes.QuoteDealType

/**
 * A change that needs confirming first, because it will either stop the quote
 * amending the schedule it was built against, or replace the quote's pricing.
 */
type PendingAmendmentChange =
  | { type: 'customer'; customerId: Customer['id'] }
  | { type: 'dealType'; dealType: QuoteDealType }
  | { type: 'effectiveFrom'; effectiveFrom: Date }
  | { type: 'overwritePricing'; amendment: ScheduleAmendment }

export type UseQuoteEditorSidebarInterface = {
  fields: {
    customer: FieldConfig<Customer['id']> & {
      isLoading: boolean
      visible: boolean
    }
    currency: FieldConfig<Currency>
    quoteDealType: FieldConfig<QuoteDealType> & {
      /**
       * The select controls keep the picked value in their own state, and only
       * re-read it when the quote's deal type changes. Cancelling the amendment
       * modal doesn't change the quote, so bumping this remounts the control and
       * drops the selection the user backed out of.
       */
      resetKey: number
    }
    validFor: Omit<FieldConfig<Duration>, 'options'> & {
      labelField: string
      labelOverride?: string
      durations: Array<{
        diff: Duration
        label: string
        value: string
      }>
      visible: boolean
    }
    effectiveFrom: {
      value: Date | undefined
      minDate: Date | undefined
      maxDate: Date | null | undefined
      visible: boolean
      isDisabled: boolean
      /**
       * The date control keeps the picked day in its own state, so bumping this
       * remounts it and drops a date the user cancelled the confirmation for.
       */
      resetKey: number
      onChange: (newValue: Date) => void
    }
    startDate: FieldConfig<Date | undefined> & {
      visible: boolean
      showField: () => void
      optional: boolean
    }
    contractLength: Omit<FieldConfig<Duration>, 'options'> & {
      durations: Array<
        | {
            diff: Duration
            label: string
            value: string
          }
        | ForeverDuration
      >
      onChange: (newValue: Duration | undefined) => void
    }
    recipients: Omit<FieldConfig<RecipientsValue>, 'options' | 'value'> & {
      value: RecipientsValue
      options: { value: string; label: string }[]
      visible: boolean
    }
    counterSigners: Omit<FieldConfig<string[]>, 'options'> & {
      options: { value: string; label: string }[]
      visible: boolean
    }
    defaultDueDateDays: FieldConfig<number | undefined> & {
      customTerms: number[]
      visible: boolean
    }
  }
  modals: {
    [AvailableModal.CreateRecipient]: {
      active: boolean
      onClose: () => void
    }
    [AvailableModal.SelectScheduleForAmendment]: {
      active: boolean
      onSubmit: (amendment: ScheduleAmendment) => Promise<boolean>
      onCancel: () => void
    }
    confirmAmendmentChange: {
      changeType: PendingAmendmentChange['type'] | null
      onConfirm: () => Promise<boolean>
      onCancel: () => void
    }
  }
  features: {
    createRecipient: {
      visible: boolean
      disabled: boolean
      onClick: () => void
    }
    isRecipientsVisible: boolean
  }
}

type UseQuoteEditorSidebar = () => UseQuoteEditorSidebarInterface

export const useQuoteEditorSidebar: UseQuoteEditorSidebar = () => {
  const cubeContext = useCubeContext()
  const { useQuoteAmendmentDealType } = useFeatureFlags()
  const [activeModal, setActiveModal] = useState<AvailableModal | null>(null)
  const [dealTypeResetKey, setDealTypeResetKey] = useState(0)
  const [effectiveFromResetKey, setEffectiveFromResetKey] = useState(0)
  const [pendingChange, setPendingChange] =
    useState<PendingAmendmentChange | null>(null)
  const [showStartDateSelection, setShowStartDateSelection] = useState(
    Boolean(cubeContext.queries.rawData.data.common.startDate)
  )
  const merchantSettings = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )
  const quoteSettings = useQuery(dashboardv20240509Client.getQuoteSettings)
  const amendedSchedule = useAmendedSchedule()
  const changeAmendmentEffectiveFrom = useChangeAmendmentEffectiveFrom()
  const shiftAmendmentEffectiveFrom = useShiftAmendmentEffectiveFrom()
  const applyScheduleAmendment = useApplyScheduleAmendment()

  const { contacts, ccContacts, counterSigners } =
    cubeContext.queries.rawData.data.quote

  const features = useMemo(() => {
    return {
      createRecipient: {
        visible: true,
        disabled: false,
        onClick: () => setActiveModal(AvailableModal.CreateRecipient)
      },
      /**
       * The "Recipients" section lists everyone the quote goes to, so somebody
       * copied in is enough to fill it - a quote can be shared with cc
       * recipients and nobody asked to sign.
       */
      isRecipientsVisible:
        contacts.length > 0 ||
        ccContacts.length > 0 ||
        counterSigners.length > 0
    }
  }, [contacts, ccContacts, counterSigners])

  const isAmendment =
    cubeContext.queries.rawData.data.quote.dealType === 'AMENDMENT'

  const applyCustomerChange = useCallback(
    async (customerId: Customer['id']) => {
      const customerResponse = await dashboardv99990101Client.getCustomer({
        id: customerId
      })

      if (!customerResponse.data) {
        return
      }

      cubeContext.mutators.updateData({
        common: {
          customerId
        },
        customers: {
          [customerId]: customerResponse.data
        },
        quote: {
          // Both recipient lists, or the old customer's cc contacts would be
          // saved against the new one and sent the quote.
          contacts: [],
          ccContacts: [],
          ...(isAmendment ? { dealType: 'NEW_BUSINESS' as const } : {})
        }
      })
    },
    [cubeContext, isAmendment]
  )

  const modals = useMemo(() => {
    return {
      [AvailableModal.CreateRecipient]: {
        active: activeModal === AvailableModal.CreateRecipient,
        onClose: () => setActiveModal(null)
      },
      [AvailableModal.SelectScheduleForAmendment]: {
        active: activeModal === AvailableModal.SelectScheduleForAmendment,
        onSubmit: async (amendment: ScheduleAmendment) => {
          // Closing the picker resets the deal type control, which is right
          // until the confirmation goes through, as nothing has changed yet.
          if (hasPricingToOverwrite(cubeContext.queries.rawData.data)) {
            setPendingChange({ type: 'overwritePricing', amendment })
            return true
          }

          return applyScheduleAmendment(amendment)
        },
        onCancel: () => {
          setActiveModal(null)
          setDealTypeResetKey(key => key + 1)
        }
      },
      confirmAmendmentChange: {
        changeType: pendingChange?.type ?? null,
        onConfirm: async () => {
          const isApplied = await match(pendingChange)
            .with({ type: 'customer' }, async ({ customerId }) => {
              await applyCustomerChange(customerId)
              return true
            })
            .with({ type: 'dealType' }, ({ dealType }) => {
              cubeContext.mutators.updateData({
                quote: {
                  dealType
                }
              })
              return true
            })
            .with({ type: 'effectiveFrom' }, ({ effectiveFrom }) =>
              changeAmendmentEffectiveFrom(effectiveFrom)
            )
            .with({ type: 'overwritePricing' }, ({ amendment }) =>
              applyScheduleAmendment(amendment)
            )
            .with(null, () => true)
            .exhaustive()

          if (!isApplied) {
            return false
          }

          setPendingChange(null)
          return true
        },
        onCancel: () => {
          match(pendingChange?.type)
            .with('dealType', 'overwritePricing', () =>
              setDealTypeResetKey(key => key + 1)
            )
            .with('effectiveFrom', () =>
              setEffectiveFromResetKey(key => key + 1)
            )
            .otherwise(() => {})

          setPendingChange(null)
        }
      }
    }
  }, [
    activeModal,
    cubeContext,
    pendingChange,
    applyCustomerChange,
    changeAmendmentEffectiveFrom,
    applyScheduleAmendment
  ])

  // Compute available counter signers from settings and quote, deduplicated by id
  const availableCounterSigners = useMemo(() => {
    const fromSettings = quoteSettings.data?.counterSigners || []
    const fromQuote =
      cubeContext.queries.rawData.data.quote.counterSigners || []
    const map = new Map<string, (typeof fromSettings)[0]>()
    ;[...fromSettings, ...fromQuote].forEach(signer => {
      if (signer.id) map.set(signer.id, signer)
    })
    return Array.from(map.values())
  }, [
    quoteSettings.data?.counterSigners,
    cubeContext.queries.rawData.data.quote.counterSigners
  ])
  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: cubeContext.queries.rawData.data.common.customerId
    },
    {
      enabled: Boolean(cubeContext.queries.rawData.data.common.customerId)
    }
  )

  const hasCustomer = Boolean(
    cubeContext.queries.rawData.data.common.customerId
  )

  const showAmendmentDealType =
    useQuoteAmendmentDealType && !cubeContext.queries.quote.isATemplate

  const fields = useMemo(() => {
    const maybeDisabledOverride = !cubeContext.queries.availableFeatures.quote
      .edit.available.enabled
      ? {
          isDisabled: true,
          disabledReason: match(
            cubeContext.queries.availableFeatures.quote.edit.reasons[0]
              ?.reasonType
          )
            .with(DisabledReasonType.CantChangeWhenArchived, () => {
              return "Can't modify an archived quote"
            })
            .with(DisabledReasonType.QuoteSuperseded, () => {
              return "Can't modify a superseded quote"
            })
            .with(DisabledReasonType.CantChangeWhenAccepted, () => {
              return "Can't modify a signed quote"
            })
            .with(DisabledReasonType.CantChangeWhenExecuted, () => {
              return "Can't modify an executed quote"
            })
            .otherwise(() => {
              return ''
            })
        }
      : {}

    const expiresAt = cubeContext.queries.rawData.data.quote.expiresAt
    const showValidUntil: boolean =
      cubeContext.queries.rawData.data.common.status !== CubeStatus.QuoteDraft

    const allCustomerContacts = customerQuery.data?.contacts || []
    const amendment = cubeContext.queries.rawData.data.quote.amendment
    const effectiveFromFeature =
      cubeContext.queries.availableFeatures.quote.editAmendmentEffectiveFrom

    return {
      recipients: {
        value: {
          contacts: cubeContext.queries.rawData.data.quote.contacts.map(
            contact => contact.contactId
          ),
          ccContacts: cubeContext.queries.rawData.data.quote.ccContacts.map(
            contact => contact.contactId
          )
        },
        onChange: (newValue: RecipientsValue) => {
          const idsToQuoteContacts = (contactIds: string[]) =>
            contactIds
              .map(contactId =>
                allCustomerContacts.find(contact => contact.id === contactId)
              )
              .filter((contact): contact is NonNullable<typeof contact> =>
                Boolean(contact)
              )
              .map(contact =>
                contactToQuoteContact({
                  ...contact,
                  customerId: cubeContext.queries.rawData.data.common.customerId
                })
              )

          cubeContext.mutators.updateData({
            quote: {
              contacts: idsToQuoteContacts(newValue.contacts),
              ccContacts: idsToQuoteContacts(newValue.ccContacts)
            }
          })
        },
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.setRecipients.available
            .enabled,
        disabledReason:
          cubeContext.queries.availableFeatures.quote.setRecipients.reasons[0]
            ?.reasonType === DisabledReasonType.CantChangeAfterPublish
            ? "Can't change recipients after a quote is published"
            : '',
        ...maybeDisabledOverride,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.quote
            .contactIds ?? [],
        options: cubeContext.queries.availableFeatures.quote.setRecipients
          .available.enabled
          ? contactsToOptions(
              allCustomerContacts,
              contact =>
                cubeContext.queries.rawData.data.quote.isSignaturesEnabled &&
                cubeContext.queries.rawData.data.quote.counterSigners
                  .map(countersigner => countersigner.email.toLowerCase())
                  .includes(contact.email.toLowerCase())
            )
          : [
              ...cubeContext.queries.rawData.data.quote.contacts,
              ...cubeContext.queries.rawData.data.quote.ccContacts
            ].map(contact => ({
              label: contact.name ?? contact.email,
              secondaryLabel: contact.name ? contact.email : undefined,
              value: contact.contactId
            })),
        visible:
          cubeContext.queries.availableFeatures.quote.setRecipients.available
            .visible
      },
      customer: {
        value: cubeContext.queries.rawData.data.common.customerId,
        onChange: async (newValue: Customer['id']) => {
          if (newValue === cubeContext.queries.rawData.data.common.customerId) {
            return
          }

          if (isAmendment) {
            setPendingChange({ type: 'customer', customerId: newValue })
            return
          }

          await applyCustomerChange(newValue)
        },
        visible:
          cubeContext.queries.availableFeatures.common.editCustomer.available
            .visible,
        isDisabled:
          !cubeContext.queries.availableFeatures.common.editCustomer.available
            .enabled,
        disabledReason:
          cubeContext.queries.availableFeatures.common.editCustomer.reasons[0]
            ?.reasonType === DisabledReasonType.CantChangeAfterPublish
            ? "Can't change customer after a quote is published"
            : '',
        ...maybeDisabledOverride,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.common
            .customerId ?? [],
        options: [],
        isLoading: false
      },
      currency: {
        value: cubeContext.queries.rawData.data.common.currency,
        onChange: (newValue: Currency) =>
          cubeContext.mutators.updateData({
            common: {
              currency: newValue
            }
          }),
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.editCurrency.available
            .enabled,
        disabledReason: undefined,
        ...maybeDisabledOverride,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.common
            .currency ?? [],
        options: cubeContext.queries.rawData.configuration.currency.enabled.map(
          currency => ({
            label: `${currency} - ${toCurrencyName(currency as Currency)}`,
            value: currency
          })
        )
      },
      quoteDealType: {
        value: cubeContext.queries.rawData.data.quote.dealType,
        onChange: (newValue: QuoteDealType) => {
          if (newValue === cubeContext.queries.rawData.data.quote.dealType) {
            return
          }

          if (isAmendment) {
            setPendingChange({ type: 'dealType', dealType: newValue })
            return
          }

          match(newValue)
            .with('AMENDMENT', () =>
              setActiveModal(AvailableModal.SelectScheduleForAmendment)
            )
            .with('NEW_BUSINESS', 'RENEWAL', () =>
              cubeContext.mutators.updateData({
                quote: {
                  dealType: newValue
                }
              })
            )
            .exhaustive()
        },
        resetKey: dealTypeResetKey,
        isDisabled: false,
        disabledReason: undefined,
        ...maybeDisabledOverride,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.quote
            .dealType ?? [],
        options: [
          {
            value: 'NEW_BUSINESS',
            label: QUOTE_DEAL_TYPE_LABELS.NEW_BUSINESS
          },
          {
            value: 'RENEWAL',
            label: QUOTE_DEAL_TYPE_LABELS.RENEWAL
          },
          ...(showAmendmentDealType
            ? [
                {
                  value: 'AMENDMENT' as const,
                  label: QUOTE_DEAL_TYPE_LABELS.AMENDMENT,
                  disabled: !hasCustomer,
                  disabledReason: hasCustomer
                    ? undefined
                    : 'Select a customer first'
                }
              ]
            : [])
        ] as Array<Option<QuoteDealType>>
      },
      validFor: {
        labelField: showValidUntil ? 'Valid until' : 'Valid for',
        labelOverride: showValidUntil
          ? expiresAt
            ? format(expiresAt, 'd MMMM yyyy')
            : undefined
          : cubeContext.queries.rawData.data.quote.expiresIn?.days
            ? `${cubeContext.queries.rawData.data.quote.expiresIn.days} ${inflect('day', cubeContext.queries.rawData.data.quote.expiresIn.days)}`
            : undefined,
        value: cubeContext.queries.rawData.data.quote.expiresIn,
        onChange: (newValue: Duration) =>
          cubeContext.mutators.updateData({
            quote: {
              expiresIn: newValue
            }
          }),
        isDisabled: false,
        disabledReason: undefined,
        ...maybeDisabledOverride,
        validationErrors: [],
        durations: [
          {
            diff: {
              days: 7
            },
            label: '7 days',
            value: '7-days'
          },
          {
            diff: {
              days: 14
            },
            label: '14 days',
            value: '14-days'
          },
          {
            diff: {
              days: 30
            },
            label: '30 days',
            value: '30-days'
          },
          {
            diff: {
              days: 45
            },
            label: '45 days',
            value: '45-days'
          },
          {
            diff: {
              days: 60
            },
            label: '60 days',
            value: '60-days'
          },
          {
            diff: {
              days: 90
            },
            label: '90 days',
            value: '90-days'
          }
        ],
        visible:
          cubeContext.queries.rawData.data.common.status ===
          CubeStatus.QuoteDraft
      },
      effectiveFrom: {
        value: amendment?.effectiveFrom,
        minDate: amendedSchedule.bounds?.minDate,
        maxDate: amendedSchedule.bounds?.maxDate,
        visible: effectiveFromFeature.available.visible && Boolean(amendment),
        isDisabled:
          !effectiveFromFeature.available.enabled ||
          amendedSchedule.isLoading ||
          Boolean(amendedSchedule.unavailableReason),
        resetKey: effectiveFromResetKey,
        onChange: (newValue: Date) => {
          const isCurrentEffectiveFrom =
            amendment && isSameDay(newValue, amendment.effectiveFrom)

          if (isCurrentEffectiveFrom) {
            return
          }

          if (shiftAmendmentEffectiveFrom(newValue)) {
            return
          }

          setPendingChange({ type: 'effectiveFrom', effectiveFrom: newValue })
        }
      },
      startDate: {
        value: cubeContext.queries.rawData.data.common.startDate,
        onChange: (newValue: Date | undefined) => {
          if (newValue) {
            setShowStartDateSelection(true)
          }

          cubeContext.mutators.updateData({
            common: {
              startDate: newValue
            }
          })
        },
        visible:
          !isAmendment &&
          (cubeContext.queries.availableFeatures.common.editStartDate.available
            .visible ||
            showStartDateSelection),
        isDisabled:
          !cubeContext.queries.availableFeatures.common.editStartDate.available
            .enabled,
        disabledReason: match(
          cubeContext.queries.availableFeatures.common.editStartDate.reasons[0]
            ?.reasonType
        )
          .with(DisabledReasonType.CantChangeAfterPublish, () => {
            return "Can't change start date after a quote is published"
          })
          .with(DisabledReasonType.CantChangeWhenArchived, () => {
            return "Can't change start date of an archived quote"
          })
          .otherwise(() => {
            return undefined
          }),
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.common
            .startDate ?? [],
        options: [],
        showField: () => {
          setShowStartDateSelection(true)
        },
        optional: (() => {
          if (
            cubeContext.queries.rawData.data.quote.isAutoExecuteQuotesEnabled
          ) {
            return false
          }

          return !cubeContext.queries.rawData.data.common.startDate
        })()
      },
      contractLength: {
        value: cubeContext.queries.totalPricingDuration,
        onChange: (newValue: Duration | undefined) => {
          const firstPhaseId = cubeContext.queries.orderedPhases[0]?.id
          cubeContext.mutators.updateData({
            phases: {
              [firstPhaseId]: {
                duration: newValue ?? 'OPEN_ENDED'
              }
            }
          })
        },
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.contractLengthEditing
            .available.enabled,
        disabledReason: '',
        ...maybeDisabledOverride,
        validationErrors: [],
        durations: [
          {
            diff: {
              months: 1,
              days: -1
            },
            label: '1 month',
            value: '1-month'
          },
          {
            diff: {
              months: 2,
              days: -1
            },
            label: '2 months',
            value: '2-months'
          },
          {
            diff: {
              months: 3,
              days: -1
            },
            label: '3 months',
            value: '3-months'
          },
          {
            diff: {
              months: 6,
              days: -1
            },
            label: '6 months',
            value: '6-months'
          },
          {
            diff: {
              months: 9,
              days: -1
            },
            label: '9 months',
            value: '9-months'
          },
          {
            diff: {
              months: 12,
              days: -1
            },
            label: '12 months',
            value: '12-months'
          },
          {
            diff: {
              months: 18,
              days: -1
            },
            label: '18 months',
            value: '18-months'
          },
          {
            diff: {
              months: 24,
              days: -1
            },
            label: '24 months',
            value: '24-months'
          },
          {
            diff: 'FOREVER',
            label: 'Open ended',
            value: 'FOREVER'
          } as ForeverDuration
        ]
      },
      counterSigners: {
        value: cubeContext.queries.rawData.data.quote.counterSigners?.map(
          signer => signer.email
        ),
        onChange: (newValue: string[]) => {
          const updatedCounterSigners = newValue.map(email => {
            // First try to find in current quote to preserve historical data
            const existingSigner =
              cubeContext.queries.rawData.data.quote.counterSigners?.find(
                s => s.email === email
              )

            if (existingSigner) {
              return existingSigner // Preserve ALL existing data
            }

            // If not in quote, try to find in available signers
            const availableSigner = availableCounterSigners.find(
              s => s.email === email
            )

            if (availableSigner) {
              return {
                email: availableSigner.email,
                name: availableSigner.name || '',
                signedAt: undefined,
                id: availableSigner.id || ''
              }
            }

            // Fallback case
            return {
              email,
              name: '',
              signedAt: undefined,
              id: ''
            }
          })

          cubeContext.mutators.updateData({
            quote: {
              ...cubeContext.queries.rawData.data.quote,
              counterSigners: updatedCounterSigners
            }
          })
        },
        visible:
          cubeContext.queries.availableFeatures.quote.editCountersigners
            .available.visible,
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.editCountersigners
            .available.enabled,
        disabledReason: '',
        ...maybeDisabledOverride,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.quote
            .counterSigners ?? [],
        options: [
          // Use deduplicated available counter signers for options
          ...availableCounterSigners.map(signer => ({
            value: signer.email,
            label: signer.name || signer.email,
            secondaryLabel: signer.name ? signer.email : undefined
          }))
        ]
      },
      defaultDueDateDays: {
        value: cubeContext.queries.rawData.data.quote.defaultDueDateDays,
        onChange: (value: number | undefined) => {
          cubeContext.mutators.updateData({
            quote: { defaultDueDateDays: value }
          })
        },
        visible:
          cubeContext.queries.availableFeatures.quote.editPaymentTerms.available
            .visible,
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.editPaymentTerms
            .available.enabled,
        disabledReason: undefined,
        ...maybeDisabledOverride,
        validationErrors: [],
        options: [],
        customTerms:
          typeof merchantSettings.data?.defaultDueDateDays !== 'undefined'
            ? [merchantSettings.data?.defaultDueDateDays]
            : []
      }
    }
  }, [
    cubeContext,
    showStartDateSelection,
    availableCounterSigners,
    merchantSettings.data?.defaultDueDateDays,
    customerQuery.data?.contacts,
    showAmendmentDealType,
    hasCustomer,
    dealTypeResetKey,
    effectiveFromResetKey,
    amendedSchedule.bounds,
    amendedSchedule.isLoading,
    amendedSchedule.unavailableReason,
    shiftAmendmentEffectiveFrom,
    isAmendment,
    applyCustomerChange
  ])

  return {
    fields,
    modals,
    features
  }
}
